import React from "react";
import styled from "styled-components";
import { FaTwitter, FaGithub, FaDiscord, FaEnvelope, FaLinkedin, FaYoutube, FaInstagram, FaFacebook } from "react-icons/fa";

const FooterContainer = styled.footer`
  color: white;
  padding: 2rem 3rem;
  box-shadow: 0 1.5px 8px #3498db;
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between; /* Ensure sections are distributed */
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    text-align: center;
  }
`;

/* Left Section - Logo & Legal */
const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    align-items: center;
    order: 1;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PictureFrame = styled.div`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: #2e3244;
  border: 2px solid #3498db;
  overflow: hidden;
  cursor: pointer;
  &:hover {
    border: 2px solid #1d73c8;
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoText = styled.a`
  text-decoration: none;
  color: #3498db;
  font-size: 1.2rem;
  font-weight: bold;

  &:hover {
    color: #1d73c8;
  }
`;

const LegalText = styled.p`
  color: #a6a6a6;
  font-size: 0.8rem; /* Compact text size */
`;

/* Middle Section - Centered Quick Links */
const MiddleSection = styled.div`
  text-align: center;
  flex-grow: 1; /* Ensures the middle section occupies the central space */

  @media (max-width: 768px) {
    margin: 1rem 0;
    order: 3;
  }
`;

const QuickLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
`;

const QuickLink = styled.a`
  color: #c1c3d7;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s;

  &:hover {
    color: #3498db;
  }
`;

/* Right Section - Socials & Contact */
const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;

  @media (max-width: 768px) {
    align-items: center;
    order: 2;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #c1c3d7;

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #3498db;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem; /* Smaller gap to align with contact items */

  svg {
    font-size: 1rem; /* Smaller size to match contact items */
  }
`;

const SocialIcon = styled.a`
  color: #c1c3d7;
  font-size: 1rem;
  transition: color 0.3s;

  &:hover {
    color: #3498db;
  }
`;

function Footer() {
  return (
    <FooterContainer>
      <FooterContent>
        {/* Left Section */}
        <LeftSection>
          <LogoContainer>
            <PictureFrame>
              <LogoImage src="assets/logotest.png" alt="Logo" />
            </PictureFrame>
            <LogoText href="#home">simonfoy.host</LogoText>
          </LogoContainer>
          <LegalText>© 2024 simonfoy.host. All rights reserved.</LegalText>
        </LeftSection>

        {/* Middle Section */}
        <MiddleSection>
          <QuickLinks>
            <QuickLink /*href=""*/>Features</QuickLink>
            <QuickLink /*href=""*/>Pricing</QuickLink>
            <QuickLink /*href=""*/>Documentation</QuickLink>
            <QuickLink /*href=""*/>Support</QuickLink>
            <QuickLink /*href=""*/>About Us</QuickLink>
          </QuickLinks>
        </MiddleSection>

        {/* Right Section */}
        <RightSection>
          <ContactInfo>
            <ContactItem>
              <FaEnvelope />
              <a href="/">support@simonfoy.host</a>
            </ContactItem>
            <ContactItem>
              <FaDiscord />
              <a href="https://discord.gg" target="_blank" rel="noopener noreferrer">
                Join Discord
              </a>
            </ContactItem>
            <SocialLinks>
            <SocialIcon
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter"
            >
              <FaTwitter />
            </SocialIcon>
            <SocialIcon
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <FaGithub />
            </SocialIcon>
            <SocialIcon
              href="https://discord.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Discord"
            >
              <FaDiscord />
            </SocialIcon>
            <SocialIcon
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <FaLinkedin />
            </SocialIcon>
            <SocialIcon
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
            >
              <FaYoutube />
            </SocialIcon>
            <SocialIcon
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <FaInstagram />
            </SocialIcon>
            <SocialIcon
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <FaFacebook />
            </SocialIcon>
          </SocialLinks>
          </ContactInfo>
        </RightSection>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;